import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  FileText, Download, FileSpreadsheet, File, ExternalLink
} from 'lucide-react';

const DOCUMENTS = [
  {
    name: "Caregiver AI Challenge Grant Application",
    file: "/Caregiver_AI_Challenge_Grant_Application.pdf",
    type: "PDF",
    category: "Grant",
    description: "15-page Phase 1 application for the Caregiver AI Challenge — workforce track covering matching/scheduling, documentation, training, coaching, predictive risk, compliance automation, and alignment with AI Principles.",
    pages: 15,
  },
  {
    name: "Caregiver AI Challenge Full Application",
    file: "/Caregiver_AI_Challenge_Full_Application.pdf",
    type: "PDF",
    category: "Grant",
    description: "Extended grant application with comprehensive technical details and supporting evidence.",
  },
  {
    name: "Caregiver AI Challenge Full Application (Word)",
    file: "/Caregiver_AI_Challenge_Full_Application.docx",
    type: "DOCX",
    category: "Grant",
    description: "Editable Word document version of the full grant application.",
  },
  {
    name: "Grant Application — AI Hospice System",
    file: "/Grant_Application_AI_Hospice_System.pdf",
    type: "PDF",
    category: "Grant",
    description: "Grant application focused on the AI-powered hospice clinical system capabilities.",
  },
  {
    name: "Grant Application — Family Caregiver Impact",
    file: "/Grant_Application_Family_Caregiver_Impact.pdf",
    type: "PDF",
    category: "Grant",
    description: "Grant application focused on family caregiver outcomes and impact measurement.",
  },
  {
    name: "Investor / Board Presentation",
    file: "/Goodwin_Hospice_Investor_Presentation.pdf",
    type: "PDF",
    category: "Investor",
    description: "Executive pitch deck covering market opportunity, product capabilities, competitive advantages, and financial projections.",
  },
  {
    name: "Product Roadmap",
    file: "/Goodwin_Hospice_Product_Roadmap.pdf",
    type: "PDF",
    category: "Strategy",
    description: "Strategic product roadmap with phased feature delivery, quick wins, and long-term vision.",
  },
  {
    name: "One-Pager & ROI Document",
    file: "/Hospice_EMR_OnePager_ROI.pdf",
    type: "PDF",
    category: "Sales",
    description: "Single-page overview with ROI projections, key features, and value proposition for prospective clients.",
  },
  {
    name: "Goodwin Hospice Story — 200 Census",
    file: "/Goodwin_Hospice_Story_200_Census.pdf",
    type: "PDF",
    category: "Sales",
    description: "Narrative case study of a 200-census hospice operation powered by the Goodwin Cares platform.",
  },
  {
    name: "Deployment Cost Estimate",
    file: "/Hospice_EMR_Deployment_Cost_Estimate.xlsx",
    type: "XLSX",
    category: "Finance",
    description: "Detailed cost breakdown for platform deployment including infrastructure, licensing, training, and ongoing operations.",
  },
];

const categoryColors = {
  Grant: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Investor: "bg-blue-100 text-blue-800 border-blue-200",
  Strategy: "bg-violet-100 text-violet-800 border-violet-200",
  Sales: "bg-amber-100 text-amber-800 border-amber-200",
  Finance: "bg-slate-100 text-slate-800 border-slate-200",
};

const typeIcons = {
  PDF: <FileText className="w-5 h-5 text-red-500" />,
  DOCX: <File className="w-5 h-5 text-blue-500" />,
  XLSX: <FileSpreadsheet className="w-5 h-5 text-green-600" />,
};

export default function BusinessDocumentsPage() {
  const handleDownload = (file) => {
    window.open(file, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6" data-testid="business-documents-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="business-docs-title">
            Business Documents & Collateral
          </h1>
          <p className="text-slate-600 mt-1">
            Grant applications, investor materials, ROI documents, and strategic collateral.
          </p>
        </div>

        <div className="grid gap-4">
          {DOCUMENTS.map((doc, i) => (
            <Card 
              key={i} 
              className="hover:shadow-md transition-shadow cursor-pointer border border-slate-200"
              data-testid={`doc-card-${i}`}
              onClick={() => handleDownload(doc.file)}
            >
              <CardContent className="flex items-center gap-4 py-4 px-5">
                <div className="shrink-0">
                  {typeIcons[doc.type]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm">{doc.name}</span>
                    <Badge variant="outline" className={`text-xs ${categoryColors[doc.category]}`}>
                      {doc.category}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {doc.type}
                    </Badge>
                    {doc.pages && (
                      <Badge variant="outline" className="text-xs text-slate-500">
                        {doc.pages} pages
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{doc.description}</p>
                </div>
                <div className="shrink-0">
                  <Download className="w-4 h-4 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
